import { useEffect, useState } from "react";
import { useTranslate } from "ra-core";
import { useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { LanguageToggle } from "../layout/LanguageToggle";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Company, Contact } from "../types";
import { renderInvoicePdf, saveBlob } from "./DownloadInvoiceButton";
import { SignaturePad } from "./SignaturePad";
import { formatUsd, itemsSubtotal, type Shipment } from "./types";

interface SigningInvoice {
  shipment: Shipment;
  company: Company;
  contact: Contact | null;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "ready"; data: SigningInvoice };

const errorText = (error: unknown): string =>
  error && typeof error === "object" && "message" in error
    ? String((error as { message: unknown }).message)
    : "Unexpected error";

// Public page reached from the one-time link staff send to a customer. No account needed:
// the token is the only key, and the database functions return just this one invoice.
export const SignInvoicePage = () => {
  const { token } = useParams<{ token: string }>();
  const translate = useTranslate();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSupabaseClient()
      .rpc("get_signing_invoice", { token })
      .then(({ data, error: rpcError }) =>
        setState(
          rpcError || !data
            ? { kind: "invalid" }
            : { kind: "ready", data: data as SigningInvoice },
        ),
      );
  }, [token]);

  const download = async () => {
    if (state.kind !== "ready") return;
    const { shipment, company, contact } = state.data;
    const { blob, fileName } = await renderInvoicePdf(
      shipment,
      company,
      contact,
    );
    saveBlob(blob, fileName);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    const { data, error: rpcError } = await getSupabaseClient().rpc(
      "sign_invoice",
      { token, name, signature, consent },
    );
    setBusy(false);
    if (rpcError) {
      setError(errorText(rpcError));
      return;
    }
    setState({ kind: "ready", data: data as SigningInvoice });
  };

  if (state.kind === "loading") {
    return <Shell>{translate("ra.page.loading")}</Shell>;
  }
  if (state.kind === "invalid") {
    return <Shell>{translate("egi.sign.invalid")}</Shell>;
  }

  const { shipment, company } = state.data;
  const subtotal = itemsSubtotal(shipment.items);
  const canSubmit = name.trim().length >= 2 && consent && !!signature && !busy;

  return (
    <Shell>
      <Card>
        <CardContent className="flex flex-col gap-5">
          <div>
            <div className="text-sm text-muted-foreground">
              {translate("egi.sign.from")}
            </div>
            <h1 className="text-xl font-semibold tabular-nums">
              {translate("egi.sign.invoice")} {shipment.invoice_number}
            </h1>
            <div className="text-sm text-muted-foreground">
              {translate("egi.sign.bill_to")}: {company.name},{" "}
              {new Date(
                `${shipment.invoice_date}T00:00:00`,
              ).toLocaleDateString()}
            </div>
          </div>

          <div className="max-h-80 overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left">
                  <th className="p-2 font-semibold">
                    {translate("egi.fields.product")}
                  </th>
                  <th className="p-2 font-semibold text-right">
                    {translate("egi.fields.quantity")}
                  </th>
                  <th className="p-2 font-semibold text-right">
                    {translate("egi.fields.amount")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {shipment.items.map((it, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="p-2">{it.description}</td>
                    <td className="p-2 text-right tabular-nums">
                      {Number(it.quantity).toLocaleString()}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {formatUsd(Number(it.quantity) * Number(it.unit_price))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={download}>
              {translate("egi.sign.download")}
            </Button>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">
                {translate("egi.fields.total")}
              </div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatUsd(subtotal + Number(shipment.tax || 0))}
              </div>
            </div>
          </div>

          {shipment.signed_at ? (
            <div className="rounded-md border border-green-600/40 bg-green-50 p-4 text-sm dark:bg-green-950/30">
              <div className="font-semibold">{translate("egi.sign.done")}</div>
              <div>
                {translate("egi.sign.signed_by", {
                  name: shipment.signer_name,
                  date: new Date(shipment.signed_at).toLocaleString(),
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4 border-t pt-4">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {translate("egi.sign.full_name")}
                </span>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </label>
              <div className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {translate("egi.sign.signature")}
                </span>
                <SignaturePad
                  onChange={setSignature}
                  clearLabel={translate("egi.sign.clear")}
                  placeholder={translate("egi.sign.draw_here")}
                />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 size-4"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>{translate("egi.sign.consent")}</span>
              </label>
              {error ? (
                <div className="text-sm text-destructive">{error}</div>
              ) : null}
              <Button onClick={submit} disabled={!canSubmit} size="lg">
                {translate(busy ? "egi.sign.signing" : "egi.sign.submit")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </Shell>
  );
};

SignInvoicePage.path = "/sign/:token";

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-muted/40">
    <header className="flex items-center justify-between bg-[#1B2B40] px-4 py-3 text-white">
      <div className="flex items-center gap-2 font-semibold">
        <span className="bg-[#FF6A00] px-1.5 text-sm">BC</span>
        Base Camp International Inc
      </div>
      <div className="[&_button]:text-white">
        <LanguageToggle />
      </div>
    </header>
    <main className="mx-auto max-w-2xl p-4">{children}</main>
  </div>
);
